import {AfterViewInit, Component, ViewChild} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {EmailEditorComponent} from 'angular-email-editor';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {JSONTemplate} from 'angular-email-editor/types';
import {BaseGridNoCookieComponent} from 'app/core-ui/abstract/basegrid/basegridnocookie.component';
import {EventsEmitterService} from 'app/core-ui/abstract/services/emitter.service';
import {LogService} from 'app/core-ui/abstract/services/log.service';
import {SecurityService} from 'app/core-ui/abstract/services/security.service';
import {VisualsService} from 'app/core-ui/abstract/services/visuals.service';
import {MediaTypes} from 'app/enums/media-types.enum';
import {NotificationBuilderDetailsService} from './notificationBuilderDetails.service';
import { ConfigService } from 'app/core-ui/abstract/services/config.service';

interface FileContents {
  accepted: File[];
  attachments: File[];
}

interface DoneParameters {
  progress: number;
  url?: string;
}

@Component({
  selector: 'notificationBuilderDetails',
  templateUrl: './notificationBuilderDetails.component.html',
  styleUrls: ['./notificationBuilderDetails.component.css'],
  providers: [NotificationBuilderDetailsService],
})
export class NotificationBuilderDetails
  extends BaseGridNoCookieComponent<NotificationBuilderDetailsService>
  implements AfterViewInit
{
  title = 'angular-email-editor';
  emailTemplate: any;
  eventType: string;
  langId: number;
  htmlContent: SafeHtml;
  htmlContentRaw: string;
  mediaType: string;
  options: any;
  isLoading: boolean = true;
  jsonTags: JSON;
  text: string;
  jsonContent: JSONTemplate;
  classicEditorMediaTypes: string[] = [
    MediaTypes.SMS,
    MediaTypes.INTERNAL,
    MediaTypes.PRX_INTERNAL,
  ];
  maxFileUploadSize: number;

  constructor(
    private notificationService: NotificationBuilderDetailsService,
    public gridService: NotificationBuilderDetailsService,
    public eventsEmitterService: EventsEmitterService,
    public securityService: SecurityService,
    private sanitizer: DomSanitizer,
    logger: LogService,
    public fb: FormBuilder,
    visualsService: VisualsService,
    public route: ActivatedRoute,
    public router: Router,
    public configService: ConfigService
  ) {
    super(securityService, logger, visualsService, gridService, 'notificationBuilderDetails');
    this.loadVisuals();
    this.eventsEmitterService.languageChange.subscribe((langId) => {
      this.loadVisuals();
      this.getValidationMessages();
    });

    this.checkboxPK = 'cookieid';
    this.maxFileUploadSize = this.configService.getNotificationFormMaxSizeFile() ?? 1000000; //1MB default
  }

  @ViewChild(EmailEditorComponent, {static: false})
  private emailEditor: EmailEditorComponent;

  ngAfterViewInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.eventType = params['eventType'];
      this.langId = params['iLangId'];
      this.mediaType = params['mediaType'];

      if (!this.eventType || !this.langId || !this.mediaType) {
        this.router.navigateByUrl('menu/7700');
      }

      this.initializeOptions().subscribe((options) => {
        this.options = options;
        this.notificationService
          .getNotificationData(this.eventType, this.langId, this.mediaType)
          .subscribe(
            (response) => {
              this.isLoading = false;
              this.jsonContent = response;
              this.getHtml(true);
            },
            (error) => {
              console.error('Error retrieving notification data:', error);
            },
          );
      });
    });
  }

  initializeOptions(): Observable<any> {
    return this.notificationService.getTags(this.eventType).pipe(
      map((response) => {
        const jsonTags = response;
        return {
          mergeTags: jsonTags,
          features: {
            stockImages: {
              enabled: false,
            },
            undoRedo: false,
            smartMergeTags: this.mediaType == 'EMAIL',
            textEditor: {
              cleanPaste: false,
            },
          },
        };
      }),
      catchError((error) => {
        console.error('Error retrieving tags data:', error);
        throw error;
      }),
    );
  }

  initEditor() {
    if (this.emailEditor) {
      if (this.classicEditorMediaTypes.includes(this.mediaType)) {
        const formattedHtml: string = this.htmlContentRaw.replace(/\r?\n/g, '<br />');
        this.emailEditor.editor.loadDesign({html: formattedHtml, classic: true} as any);
      } else {
        this.emailEditor.editor.loadDesign(this.jsonContent);
        this.emailEditor.editor.addEventListener('design:updated', function (data) {
          // Design is updated by the user
          const type = data.type; // body, row, content
          const item = data.item;
          const changes = data.changes;
          console.log('design:updated', type, item, changes);
        });
      }
    }

    this.emailEditor.editor.registerCallback('image', (file: FileContents, done: (object: DoneParameters) => void) => {
      //start progress bar
      done({progress: 10});
      const fileUploaded = file.accepted[0];
      if(fileUploaded && fileUploaded.size <= this.maxFileUploadSize){
        const reader = new FileReader();
        reader.readAsDataURL(fileUploaded);
        reader.onloadend = () => {
          //complete progress bar and return image src to editor
          done({progress: 100, url: reader.result.toString()});
        }
      }
      done({ progress: 100, url: "Error uploading image, size must be under 1 MB."});
    });
  }

  optionsLoaded() {
    return {
      displayMode: 'web',
      contentWidth: '700px',
      backgroundColor: '#ffffff',
    };
  }
  tools() {
    return {};
  }

  exportHtml() {
    this.emailEditor.editor.exportHtml(
      (data) =>
        this.notificationService
          .updateHtmlContent({
            eventType: this.eventType,
            iLangId: this.langId,
            mediaType: this.mediaType,
            htmlContent: data.html,
          })
          .subscribe(
            (response) => {
              console.log('Email template saved successfully - HTML');
            },
            (error) => {
              console.error('Error saving email template:', error);
            },
          ),
      {
        cleanup: true,
      },
    );
  }

  appearance() {
    return {
      theme: 'dark',
      panels: {
        tools: {
          dock: 'right',
        },
      },
    };
  }

  saveJSON() {
    this.emailEditor.editor.saveDesign((data) =>
      this.notificationService
        .updateJsonContent({
          eventType: this.eventType,
          iLangId: this.langId,
          mediaType: this.mediaType,
          jsonContent: JSON.stringify(data, null, 2),
        })
        .subscribe(
          (response) => {
            console.log('Email template saved successfully - JSON');
            this.getHtml();
          },
          (error) => {
            console.error('Error saving email template:', error);
          },
        ),
    );
    this.exportHtml();
  }

  getHtml(initEditor: boolean = false) {
    this.gridService.getHtmlData(this.eventType, this.langId, this.mediaType).subscribe(
      (response) => {
        this.htmlContent = this.sanitizer.bypassSecurityTrustHtml(response.toString());
        this.htmlContentRaw = response.toString();
        if (initEditor) {
          this.initEditor();
        }
      },
      (error) => {
        console.error('Error retrieving HTML data:', error);
      },
    );
  }
}
